"use client";

import dynamic from "next/dynamic";
import { Component, useState, useSyncExternalStore, type ReactNode } from "react";
import { Board2D, type BoardProps } from "./Board2D";
import { AppearanceControls } from "./AppearanceControls";
import { BOARDS } from "@/lib/appearance";
import { useAppearance } from "@/components/site/AppearanceProvider";
import type { CameraPreset } from "@/components/play/preferences";
import { useEngaged } from "@/lib/engagement";

const Board3D = dynamic(() => import("@/components/board3d/Board3D"), {
  ssr: false,
  loading: () => <div className="board-loading"><span className="loading-orbit"/>Preparing your chess set…</div>,
});

function detectWebGL(): boolean {
  try {
    const c = document.createElement("canvas");
    const gl = c.getContext("webgl2");
    if (!gl) return false;
    gl.getExtension("WEBGL_lose_context")?.loseContext();
    return true;
  } catch { return false; }
}
let webgl: boolean | null = null;
const subscribe = () => () => {};

class SceneBoundary extends Component<{ children: ReactNode; fallback: ReactNode; onError: () => void }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch() { this.props.onError(); }
  render() { return this.state.failed ? <><p className="mb-2 text-sm text-muted">3D is unavailable on this device. Your 2D board is ready.</p>{this.props.fallback}</> : this.props.children; }
}

export function BoardView({ view = "3d", onViewChange, controls = true, cinematic = false, cameraPreset, resetKey = 0, ...props }: BoardProps & { view?: "3d" | "2d"; onViewChange?: (view: "2d" | "3d") => void; controls?: boolean; cinematic?: boolean; cameraPreset?: CameraPreset; resetKey?: number }) {
  const { appearance } = useAppearance();
  const [local, setLocal] = useState<{ source: string; view: "3d" | "2d" } | null>(null);
  const active = local?.source === view ? local.view : view;
  const [materialsOpen, setMaterialsOpen] = useState(false);
  const [cameraKey, setCameraKey] = useState(0);
  // The 3D scene, and the WebGL probe before it, wait for the reader's first interaction;
  // until then supported stays null and the placeholder keeps the board's space.
  const engaged = useEngaged();
  const detected = useSyncExternalStore(subscribe, () => (engaged ? (webgl ??= detectWebGL()) : null), () => null);
  // After the 3D scene crashes, the toolbar and instructions follow the 2D board shown in its place.
  const [crashed, setCrashed] = useState(false);
  const supported = crashed ? false : detected;
  const change = (mode: "3d" | "2d") => { if (onViewChange) { setLocal(null); onViewChange(mode); } else setLocal({ source: view, view: mode }); };
  return <div className={`board-experience ${cinematic ? "cinematic-board" : ""}`}>
    {controls && <div className="board-toolbar">
      <div className="segmented" aria-label="Board view">{(["2d", "3d"] as const).map((mode) => <button type="button" key={mode} aria-pressed={(supported === false ? "2d" : active) === mode} disabled={mode === "3d" && supported === false} onClick={() => change(mode)}>{mode.toUpperCase()}</button>)}</div>
      <button type="button" className="board-material-button" onClick={() => setMaterialsOpen(!materialsOpen)} aria-expanded={materialsOpen}><span className="material-dot" style={{ background: BOARDS[appearance.board].dark }}/>{BOARDS[appearance.board].name}<span aria-hidden>⌄</span></button>
      {active === "3d" && supported && <button type="button" className="camera-reset" onClick={() => setCameraKey((k) => k + 1)} aria-label="Reset camera" title="Reset camera">↺</button>}
    </div>}
    {materialsOpen && <div className="board-material-panel"><AppearanceControls compact/></div>}
    <SceneBoundary fallback={<Board2D {...props}/>} onError={() => setCrashed(true)}>
      {active === "3d" && supported === null ? <div className="board-loading"><span className="loading-orbit" />Preparing your chess set…</div> : active === "3d" && supported ? <Board3D {...props} cameraKey={cameraKey + resetKey} cinematic={cinematic} cameraPreset={cameraPreset}/> : <Board2D {...props}/>}
    </SceneBoundary>
    {controls && <p className="board-instructions">{active === "3d" && supported ? "Drag to orbit · Scroll to zoom · Click a piece to move" : "Click or drag to move · Arrow keys + Enter to play"}</p>}
  </div>;
}
